/**
 * useTechAwareScanRouting — cross-tech scan redirect between the DTG and DTF
 * print stations (Phase 50.01 / BLU-233).
 *
 * Both print stations stay separate, but a garment scan routes the operator to
 * the CORRECT station for its print technology:
 *   - DTG print station scans a DTF garment → opens the Film-Print station.
 *   - DTF Film-Print station scans a DTG garment → opens the DTG print station.
 *
 * The hook returns `handleCrossTechScan(qrCode)`, a QR-only heuristic the
 * station can call FIRST in its scan handler when it has no reason to doubt
 * the label. If it returns `true`, it has already given cross-tech feedback
 * (audible buzz + visual modal) and auto-navigated to the correct station
 * carrying `?garment=<id>` as context; the station should bail out of its own
 * scan handling. If it returns `false`, the scan belongs on this station (or
 * isn't a garment QR) and the station proceeds as normal.
 *
 * DB-trust note (2026-08-08 incident — 6 Mini Zali CBP-CHOC units re-stamped
 * DTG→DTF post-print): a label's `m` field is baked at label-print time and
 * goes stale the moment the unit's DB print_technology is corrected
 * afterward. `handleCrossTechScan`'s QR-only decision must NEVER be trusted
 * as the final word for an ALREADY-KNOWN garment (loaded batch data, an
 * org-scoped lookup) — callers with DB access should skip it and, once the
 * DB itself confirms the mismatch, call `bounceToTech` directly.
 * `handleCrossTechScan` stays useful only as the zero-latency first guess
 * before any DB round trip has happened.
 *
 * Hook-stability discipline (plan .3 review lessons):
 *  - `useScanFeedback`'s returned object is NOT memoized (`modalProps` is a
 *    fresh literal every render). We capture it in a ref so it never enters a
 *    deps array.
 *  - `react-router`'s `navigate` is stable across renders but we still read it
 *    via a ref to keep the returned callback's deps empty → stable identity, so
 *    it's safe to pass into the station's own `useCallback` deps.
 *  - The returned callback is wrapped in `useCallback([])` so its identity never
 *    changes.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { evaluateScanRoute, buildTechRoutePath, printTechnologyRoute, type PrintTechnology } from '../utils/print-tech-routing';
import { extractGarmentId } from '@/lib/qr-utils';
import { useRole } from '@/hooks/use-role';
import { isRouteAllowedForRole } from '@/lib/roles';

export interface TechAwareScanRouting {
  /**
   * Evaluate a scanned QR for a cross-tech redirect. QR-only — see the
   * DB-trust note above before calling this for a garment the caller could
   * instead check/resolve against the DB.
   * @returns `true` if the scan was a cross-tech garment and the operator was
   *   redirected (caller should stop). `false` to let the caller proceed.
   * @deprecated both stations (PrintStationPage, FilmPrintPage) now resolve
   *   every scan against the DB-authoritative guard (DTF_PRODUCT /
   *   NOT_DTF_UNIT) and call `bounceToTech` directly per the DB-trust note
   *   above — neither has called this QR-only heuristic since the
   *   2026-08-08 stale-label fix. Kept for the hook-stability tests; not
   *   wired to a live scan handler. Do not add a new caller — resolve
   *   against the DB and call `bounceToTech` instead.
   */
  handleCrossTechScan: (qrCode: string) => boolean;
  /**
   * Perform the cross-tech redirect UX (audible + visual feedback, then
   * navigate) for an ALREADY-CONFIRMED tech — no QR decoding involved. Use
   * this once the DB (not the scanned label) has told the caller a garment
   * belongs on the OTHER station.
   */
  bounceToTech: (tech: PrintTechnology, garmentId?: string | null) => void;
  /**
   * Open the other station for a garment with NO feedback of its own: the
   * caller has already told the operator (the print technology mismatch
   * dialog), so a second buzz would read as a second problem. A no-op for a
   * role that cannot open that station; check `canGoToTech` first.
   */
  goToTech: (tech: PrintTechnology, garmentId?: string | null) => void;
  /** Whether this role may open the station for `tech` (DTF Printer cannot open DTG). */
  canGoToTech: (tech: PrintTechnology) => boolean;
}

/**
 * @param stationTech the print technology this station serves ('DTG' for the
 *   DTG print station, 'DTF' for the Film-Print station).
 * @param feedback the station's existing `useScanFeedback` instance — reused so
 *   we never stack a second modal (one blocking modal at a time per
 *   PRODUCTION_DESIGN.md §6.1).
 */
export function useTechAwareScanRouting(
  stationTech: PrintTechnology,
  feedback: ReturnType<typeof useScanFeedback>,
): TechAwareScanRouting {
  const { t } = useTranslation('production');
  const navigate = useNavigate();

  // Refs for the two unstable / identity-sensitive deps so the returned
  // callback can have empty deps (stable identity).
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });

  const feedbackRef = useRef(feedback);
  useEffect(() => {
    feedbackRef.current = feedback;
  });

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });

  const { role } = useRole();
  const roleRef = useRef(role);
  useEffect(() => {
    roleRef.current = role;
  });

  // A role held to a route allowlist (DTF Printer) cannot open the other
  // station. Reads the role per render, so the dialog's button is right.
  const canGoToTech = useCallback(
    (tech: PrintTechnology) => isRouteAllowedForRole(role, printTechnologyRoute(tech)),
    [role],
  );

  const goToTech = useCallback((tech: PrintTechnology, garmentId?: string | null) => {
    if (!isRouteAllowedForRole(roleRef.current, printTechnologyRoute(tech))) return;
    navigateRef.current(buildTechRoutePath(tech, garmentId ?? null));
  }, []);

  // Cross-tech redirect UX for an ALREADY-CONFIRMED tech (see DB-trust note
  // above): clear audible + visual confirmation, then auto-navigate to the
  // correct station carrying the garment context. We reuse the station's
  // scan-feedback modal (warning severity → buzz + amber) rather than
  // stacking a fresh one. The modal is informational and non-blocking to the
  // redirect — navigation fires immediately after. Stable identity (refs
  // only) so callers can safely put it in their own useCallback deps.
  const bounceToTech = useCallback((tech: PrintTechnology, garmentId?: string | null) => {
    const translate = tRef.current;
    const targetLabel =
      tech === 'DTF' ? translate('scanRouting.toFilmPrint') : translate('scanRouting.toPrinter');
    feedbackRef.current.triggerWarning(
      translate('scanRouting.crossTechTitle', { tech }),
      translate('scanRouting.crossTechBody', { station: targetLabel }),
    );
    // The warning still plays for a role that cannot open the other station;
    // there is just nowhere to send it.
    goToTech(tech, garmentId);
  }, [goToTech]);

  const handleCrossTechScan = useCallback(
    (qrCode: string): boolean => {
      // Canonical garment-id decode (handles V1/V2 short-key + legacy formats).
      const garmentId = extractGarmentId(qrCode);
      const evaluation = evaluateScanRoute(stationTech, qrCode, garmentId);
      if (!evaluation.crossTech) return false;
      bounceToTech(evaluation.tech, evaluation.garmentId);
      return true;
    },
    // stationTech is a primitive prop; bounceToTech is stable.
    [stationTech, bounceToTech],
  );

  return { handleCrossTechScan, bounceToTech, goToTech, canGoToTech };
}
